import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { Loading } from "app/components/Wrappers";
import ScoreCard from "./ScoreCard";
import { FilterState } from "./ScoresFilterModal/utils";

interface Props {
  isModalOpen: boolean;
  athleteId: string;
  openScoreModal: (score: any) => void;
  filters: FilterState;
  athlete: any;
}

const Scores = ({ isModalOpen, athleteId, openScoreModal, filters, athlete }: Props) => {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [scores, setScores] = useState([]);
  const loaderRef = useRef(null);
  const listContainerRef = useRef(null);

  const renderScoreCards = () => {
    if (loading && scores.length === 0) {
      return <Loading isLoading loadType="spinner" />;
    }

    if (scores.length === 0 && !loading) {
      return (
        <div className={styles.no_scores_container}>
          <i className='ico ico-analytics' />
          <p className={styles.no_events_title}>{t("No scores posted")}</p>
          <p className={styles.no_events_subtitle}>{t("All your scores will appear here.")}</p>
        </div>
      );
    }

    return scores.map((score, index) => (
      <ScoreCard key={index} score={score} onOpenModal={() => openScoreModal(score)} />
    ));
  };

  const fetchScores = useCallback(async () => {
    if (loading || !isModalOpen) { return; }

    if (!hasMore) { return; }

    setLoading(true);

    schedulingFetchGet(`/student_profiles/${athleteId}/scores`, {
      page,
      fromDate: filters.attendance?.range?.start.toISO(),
      toDate: filters.attendance?.range?.end.toISO(),
      courseId: filters.course,
      teeId: filters.tee,
    })
      .then((response) => {
        setScores(prev => [...prev, ...(response.data || [])]);
        setMeta(response.meta.stats);
        setHasMore(page * 20 < response.meta.stats.count);
        setPage(prev => prev + 1);
      })
      .catch((error) => {
        console.error(error);
        setHasMore(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [athleteId, loading, hasMore, page, filters, isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) { return; }

    fetchScores();
  }, [isModalOpen, fetchScores]);

  useEffect(() => {
    if (!isModalOpen) {
      return;
    }

    setScores([]);
    setPage(1);
    setHasMore(true);
  }, [filters, isModalOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !loading) {
          if (hasMore) {
            fetchScores();
          }
        }
      },
      { threshold: 0.1, rootMargin: '10px' }
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      if (currentLoaderRef) {
        observer.unobserve(currentLoaderRef);
      }
    };
  }, [fetchScores, hasMore, loading]);

  return (
    <>
      <div className={styles.metadata}>
        {t('score_summary', {
          score: meta?.count || '-',
          max: meta?.max || '-',
          min: meta?.min || '-',
          avg: meta?.avg || '-',
          handicap_index: athlete?.handicapIndexDisplay || '-',
          low_hi: athlete?.lowHandicapIndexDisplay || '-',
        })}
      </div>

      <div className={styles.list_container} ref={listContainerRef}>
        {renderScoreCards()}

        {hasMore && (
          <div ref={loaderRef} className={styles.loader_observer_spacer} />
        )}
      </div>
      <div id="datepicker-portal"></div>
    </>
  );
};

export default Scores;
